import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { maturityAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useRBAC } from '../../contexts/RBACContext';
import { usePortal } from '../../contexts/PortalContext';
import { GlobalContextSelector } from './GlobalContextSelector';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Shield,
  AlertTriangle,
  ClipboardList,
  Settings,
  Users,
  ChevronDown,
  ChevronRight,
  Sun,
  Moon,
  LogOut,
  Menu,
  X,
  Database,
  FileWarning,
  UserCheck,
  TrendingUp,
  Briefcase,
  FileQuestion,
  Send,
  BookOpen,
  CheckSquare,
  FolderOpen,
  Activity,
  History,
  BarChart3,
  Sliders,
  Network,
  Target,
  ClipboardCheck,
  ShieldCheck,
  FileSearch,
  Bell,
  Palette,
  Globe,
  MailX
} from 'lucide-react';

// Module to path mapping for RBAC filtering
const MODULE_PATHS = {
  ropa: ['/compliance/ropa'],
  data_flow: ['/compliance/data-flows'],
  dsr: ['/compliance/dsr'],
  breach: ['/compliance/breaches'],
  vendor: ['/compliance/vendors'],
  maturity: ['/compliance/maturity'],
  due_diligence: ['/compliance/due-diligence'],
  staff_assessment: ['/compliance/questionnaires'],
  readiness: ['/compliance/readiness'],
  risk_assessment: ['/risk/assessments', '/risk/dpia', '/risk/ai'],
  risk_register: ['/risk/register'],
  tasks: ['/operations/tasks'],
  documents: ['/operations/documents'],
};

// Full menu structure with module requirements - RESTRUCTURED
const fullMenuItems = {
  dpo: [
    // Core (always visible)
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    // Compliance
    {
      label: 'Compliance',
      icon: Shield,
      children: [
        { label: 'Maturity Assessment', icon: Target, path: '/compliance/maturity', module: 'maturity' },
        { label: 'ROPA', icon: Database, path: '/compliance/ropa', module: 'ropa' },
        { label: 'Data Flow Map', icon: Network, path: '/compliance/data-flows', module: 'data_flow' },
        { label: 'Data Breach', icon: FileWarning, path: '/compliance/breaches', module: 'breach' },
        { label: 'DSR Management', icon: UserCheck, path: '/compliance/dsr', module: 'dsr' },
        { label: 'Vendor Management', icon: Briefcase, path: '/compliance/vendors', module: 'vendor' },
      ]
    },
    // Assessments (NEW top-level)
    {
      label: 'Assessments',
      icon: ClipboardCheck,
      children: [
        { label: 'Due Diligence', icon: FileSearch, path: '/compliance/due-diligence', module: 'due_diligence' },
        { label: 'Staff Assessments', icon: ClipboardList, path: '/compliance/questionnaires', module: 'staff_assessment' },
        { label: 'Readiness Assessment', icon: CheckSquare, path: '/compliance/readiness', module: 'readiness' },
      ]
    },
    // Audit & Assurance (NEW)
    {
      label: 'Audit & Assurance',
      icon: ShieldCheck,
      children: [
        { label: 'Risk Assessments', icon: AlertTriangle, path: '/risk/assessments', module: 'risk_assessment' },
        { label: 'Risk Register', icon: FileText, path: '/risk/register', module: 'risk_register' },
      ]
    },
    // Operations
    {
      label: 'Operations',
      icon: Activity,
      children: [
        { label: 'Tasks', icon: ClipboardList, path: '/operations/tasks', module: 'tasks' },
        { label: 'Documents', icon: FolderOpen, path: '/operations/documents', module: 'documents' },
      ]
    },
    // Administration
    {
      label: 'Administration',
      icon: Settings,
      children: [
        { label: 'Platform Admin', icon: Globe, path: '/admin/platform', roles: ['super_admin'] },
        { label: 'MFA Administration', icon: Shield, path: '/admin/mfa', roles: ['super_admin'] },
        { label: 'MFA Management', icon: Shield, path: '/admin/org-mfa', roles: ['dpo', 'client_admin'] },
        { label: 'System Settings', icon: Sliders, path: '/admin/system-settings' },
        { label: 'Client Management', icon: Building2, path: '/admin/clients' },
        { label: 'User Management', icon: Users, path: '/admin/users' },
        { label: 'Temp Guest Access', icon: Users, path: '/admin/temp-guests', roles: ['dpo', 'super_admin', 'client_admin', 'entity_admin'] },
        { label: 'Audit Trail', icon: History, path: '/admin/audit' },
        { label: 'Reports', icon: BarChart3, path: '/admin/reports' },
        { label: 'Risk Settings', icon: Sliders, path: '/admin/risk-settings' },
      ]
    },
    // Settings (Personal)
    {
      label: 'Settings',
      icon: Sliders,
      children: [
        { label: 'Notifications', icon: Bell, path: '/settings/notifications' },
        { label: 'Portal Branding', icon: Palette, path: '/settings/branding' },
      ]
    },
  ],
  client: [
    // Core
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    // Compliance
    {
      label: 'Compliance',
      icon: Shield,
      children: [
        { label: 'Maturity Assessment', icon: Target, path: '/compliance/maturity', module: 'maturity' },
        { label: 'ROPA', icon: Database, path: '/compliance/ropa', module: 'ropa' },
        { label: 'Data Flow Map', icon: Network, path: '/compliance/data-flows', module: 'data_flow' },
        { label: 'Data Breach', icon: FileWarning, path: '/compliance/breaches', module: 'breach' },
        { label: 'DSR Management', icon: UserCheck, path: '/compliance/dsr', module: 'dsr' },
        { label: 'Vendor Management', icon: Briefcase, path: '/compliance/vendors', module: 'vendor' },
      ]
    },
    // Assessments
    {
      label: 'Assessments',
      icon: ClipboardCheck,
      children: [
        { label: 'Due Diligence', icon: FileSearch, path: '/compliance/due-diligence', module: 'due_diligence' },
        { label: 'Staff Assessments', icon: ClipboardList, path: '/compliance/questionnaires', module: 'staff_assessment' },
        { label: 'Readiness Assessment', icon: CheckSquare, path: '/compliance/readiness', module: 'readiness' },
      ]
    },
    // Audit & Assurance
    {
      label: 'Audit & Assurance',
      icon: ShieldCheck,
      children: [
        { label: 'Risk Assessments', icon: AlertTriangle, path: '/risk/assessments', module: 'risk_assessment' },
        { label: 'Risk Register', icon: FileText, path: '/risk/register', module: 'risk_register' },
      ]
    },
    // Operations
    {
      label: 'Operations',
      icon: Activity,
      children: [
        { label: 'Tasks', icon: ClipboardList, path: '/operations/tasks', module: 'tasks' },
        { label: 'Documents', icon: FolderOpen, path: '/operations/documents', module: 'documents' },
      ]
    },
    // Administration (Task 3: Limited for Client Admin/Entity Admin)
    {
      label: 'Administration',
      icon: Settings,
      children: [
        { label: 'MFA Management', icon: Shield, path: '/admin/org-mfa', roles: ['dpo', 'client_admin', 'entity_admin'] },
        { label: 'User Management', icon: Users, path: '/admin/users', roles: ['dpo', 'client_admin', 'entity_admin'] },
        { label: 'Email Suppression', icon: MailX, path: '/admin/email-suppression', roles: ['dpo'] },
        { label: 'Temp Guest Access', icon: Users, path: '/admin/temp-guests', roles: ['dpo', 'super_admin', 'client_admin', 'entity_admin'] },
      ]
    },
    // Settings (Personal)
    {
      label: 'Settings',
      icon: Sliders,
      children: [
        { label: 'Notifications', icon: Bell, path: '/settings/notifications' },
      ]
    },
  ]
};

function MenuItem({ item, isCollapsed }) {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const isActive = location.pathname === item.path;
  const isChildActive = hasChildren && item.children.some(child => 
    location.pathname === child.path || location.pathname.startsWith(child.path + '/')
  );
  
  // Keep menu open if any child is active
  const [isOpen, setIsOpen] = useState(isChildActive);
  
  // Update isOpen when child becomes active (e.g., navigation)
  React.useEffect(() => {
    if (isChildActive && !isOpen) {
      setIsOpen(true);
    }
  }, [isChildActive, location.pathname]);

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`sidebar-item w-full justify-between ${isChildActive ? 'active' : ''}`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <span className="flex items-center gap-3">
            <item.icon className="h-4 w-4" />
            {!isCollapsed && <span>{item.label}</span>}
          </span>
          {!isCollapsed && (
            isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
          )}
        </button>
        {isOpen && !isCollapsed && (
          <div className="ml-4 mt-1 space-y-1">
            {item.children.map((child) => {
              const childActive = location.pathname === child.path || 
                location.pathname.startsWith(child.path + '/');
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  className={`sidebar-item ${childActive ? 'active' : ''}`}
                  data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <child.icon className="h-4 w-4" />
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      className={`sidebar-item ${isActive ? 'active' : ''}`}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <item.icon className="h-4 w-4" />
      {!isCollapsed && <span>{item.label}</span>}
    </Link>
  );
}

// Filter menu items based on enabled modules
function filterMenuItems(items, enabledModules, isDPO, userRole) {
  return items.map(item => {
    // If item has children, filter them
    if (item.children) {
      const filteredChildren = item.children.filter(child => {
        // Check role-based access first
        if (child.roles && !child.roles.includes(userRole)) {
          return false;
        }
        // If child requires a specific module
        if (child.module) {
          return isDPO || enabledModules.includes(child.module);
        }
        // If child requires any of multiple modules
        if (child.modules) {
          return isDPO || child.modules.some(m => enabledModules.includes(m));
        }
        // No module requirement, show it
        return true;
      });

      // Only return parent if it has visible children
      if (filteredChildren.length > 0) {
        return { ...item, children: filteredChildren };
      }
      return null;
    }

    // Top-level items - check role requirements
    if (item.roles && !item.roles.includes(userRole)) {
      return null;
    }

    // Top-level items without module requirements are always shown
    return item;
  }).filter(Boolean); // Remove null items
}

/**
 * Spec §4 — Snapshot prompt on session completion.
 * Fires the snapshot toast when the user navigates OUT of
 * /compliance/maturity/* within the app, if they assessed at least one
 * control in the current browser session and the prompt has not yet shown.
 * Guarded by the same `snapshotPromptShown` sessionStorage flag used by the
 * maturity pages so it still fires at most once per session.
 */
function SnapshotPromptOnLeaveMaturity() {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    const wasInMaturity = prev.startsWith('/compliance/maturity');
    const isInMaturity = next.startsWith('/compliance/maturity');

    if (wasInMaturity && !isInMaturity) {
      const hasAssessed = sessionStorage.getItem('maturityAssessedInSession') === 'true';
      const alreadyPrompted = sessionStorage.getItem('snapshotPromptShown') === 'true';
      const entityId = sessionStorage.getItem('maturityAssessedEntityId');
      if (hasAssessed && !alreadyPrompted && entityId) {
        sessionStorage.setItem('snapshotPromptShown', 'true');
        toast('Assessment session complete', {
          description: 'Save a snapshot to record your current score and track progress over time.',
          duration: Infinity,
          action: {
            label: 'Save Snapshot',
            onClick: async () => {
              try {
                await maturityAPI.createSnapshot(entityId);
                toast.success('Snapshot saved successfully');
              } catch (err) {
                toast.error('Failed to save snapshot');
              }
            },
          },
          cancel: { label: 'Not Now', onClick: () => {} },
        });
      }
    }
    prevPathRef.current = next;
  }, [location.pathname]);

  return null;
}

export function DashboardLayout({ children }) {
  const { user, logout, isDPO, isSuperAdmin, isClientAdmin, isEntityAdmin, isViewer } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { enabledModules } = useRBAC();
  const { isPortalMode, clientInfo, getBrandingColor, getLogo } = usePortal();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get base menu items based on role (Sprint 1 RBAC)
  // DPO role includes both dpo and super_admin
  const baseItems = isDPO ? fullMenuItems.dpo : fullMenuItems.client;

  // Filter menu items based on enabled modules and user role
  const items = useMemo(() => {
    // Filter based on role and modules
    let filtered = filterMenuItems(baseItems, enabledModules, isDPO, user?.role);

    // Remove admin-only sections for non-super-admin DPOs (Sprint 1 spec section 4)
    // Super Admin can see: Platform Admin, MFA Administration, System Settings, Audit Trail, Portal Branding, Risk Settings
    // DPO (non-super) can see: MFA Management, Client Management, User Management, Reports
    if (isDPO && !isSuperAdmin && filtered.length > 0) {
      filtered = filtered.map(item => {
        if (item.label === 'Administration') {
          const adminChildren = item.children.filter(child => {
            // DPO can see: MFA Management, Client Management, User Management, Reports
            const allowedForDPO = ['MFA Management', 'Client Management', 'User Management', 'Reports', 'Temp Guest Access', 'Email Suppression'];
            return allowedForDPO.includes(child.label);
          });
          return adminChildren.length > 0 ? { ...item, children: adminChildren } : null;
        }
        if (item.label === 'Settings') {
          // DPO can see: Notifications (Portal Branding is Super Admin only)
          const settingsChildren = item.children.filter(child => child.label !== 'Portal Branding');
          return settingsChildren.length > 0 ? { ...item, children: settingsChildren } : null;
        }
        return item;
      }).filter(Boolean);
    }

    // Task 3: For Client Admin and Entity Admin: filter Administration section to only allowed items
    // Viewers: remove admin sections entirely
    if (!isDPO && filtered.length > 0) {
      filtered = filtered.map(item => {
        // For Administration: keep it but filter children based on role
        if (item.label === 'Administration') {
          if (isViewer) {
            return null; // Viewers have no admin access
          }
          // Client Admin and Entity Admin keep filtered children (already filtered by roles array)
          return item;
        }
        // Settings: remove entirely for non-DPO
        if (item.label === 'Settings') return null;
        return item;
      }).filter(Boolean);
    }

    return filtered;
  }, [baseItems, enabledModules, isDPO, isSuperAdmin, user?.role]);

  const handleLogout = () => {
    // Clear portal mode on logout
    sessionStorage.removeItem('portal_subdomain');
    sessionStorage.removeItem('global_context');
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  // Get portal branding
  const primaryColor = isPortalMode ? getBrandingColor('primary') : null;
  const logo = isPortalMode ? getLogo() : null;
  const portalName = isPortalMode ? (clientInfo?.client_name || 'Client Portal') : 'DataShield';

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <SnapshotPromptOnLeaveMaturity />
      {/* Deep Nebula Glow Layer - Fixed Background */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 140% 100% at 50% -30%, rgba(139, 92, 246, 0.35) 0%, transparent 55%),
            radial-gradient(ellipse 100% 80% at 100% -10%, rgba(168, 85, 247, 0.25) 0%, transparent 45%),
            radial-gradient(ellipse 90% 70% at 0% 0%, rgba(124, 58, 237, 0.2) 0%, transparent 40%),
            radial-gradient(ellipse 120% 60% at 50% 110%, rgba(91, 33, 182, 0.15) 0%, transparent 50%)
          `,
          zIndex: 1
        }}
        aria-hidden="true"
      />
      
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 border-b bg-background/80 backdrop-blur-md z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          {logo ? (
            <img src={logo} alt={portalName} className="h-8 object-contain" />
          ) : (
            <span className="font-semibold text-lg" style={primaryColor ? { color: primaryColor } : {}}>
              {portalName}
            </span>
          )}
          {isPortalMode && (
            <Badge variant="outline" className="text-xs">Portal</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-mobile">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-card border-r z-50
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'w-64' : 'w-16'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ zIndex: 50 }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`h-20 flex items-center border-b px-4 ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
            {sidebarOpen && (
              <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
                {logo ? (
                  <img src={logo} alt={portalName} className="h-12 object-contain" />
                ) : (
                  <img 
                    src="/fulllogo_transparent.png" 
                    alt="Assura" 
                    className="h-12 object-contain dark:brightness-0 dark:invert" 
                  />
                )}
                {isPortalMode && (
                  <Badge variant="secondary" className="text-xs ml-1">Portal</Badge>
                )}
              </Link>
            )}
            {!sidebarOpen && (
              <Link to="/dashboard" className="flex items-center justify-center" data-testid="logo-link-collapsed">
                <img 
                  src="/icononly_transparent.png" 
                  alt="Assura" 
                  className="h-10 w-10 object-contain dark:brightness-0 dark:invert" 
                />
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="sidebar-toggle"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-2 space-y-1">
              {items.map((item) => (
                <MenuItem key={item.label} item={item} isCollapsed={!sidebarOpen} />
              ))}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="border-t p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`w-full ${sidebarOpen ? 'justify-start' : 'justify-center'}`}
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials(user?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  {sidebarOpen && (
                    <div className="ml-3 text-left flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user?.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate capitalize">{user?.role?.replace('_', ' ')}</p>
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/account/security')} data-testid="mfa-settings-btn">
                  <Shield className="mr-2 h-4 w-4" />
                  Security &amp; MFA
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle-dropdown">
                  {theme === 'dark' ? (
                    <>
                      <Sun className="mr-2 h-4 w-4" />
                      Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="mr-2 h-4 w-4" />
                      Dark Mode
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`
          transition-all duration-300 ease-in-out
          pt-16 lg:pt-0
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}
          relative z-10
        `}
      >
        <div className="min-h-screen">
          <GlobalContextSelector />
          {children}
        </div>
      </main>
    </div>
  );
}

export default DashboardLayout;